import {
    forwardRef,
    useMemo,
    useId
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IColorSwatchProps from "./type";
import stylesheet, {
    getColorSwatchSize,
    useStyles
} from "./stylesheet";
import {
    getColorSwatchInkIsLight,
    parseColorSwatchColor,
    getColorSwatchIsEdge,
    getColorSwatchLabel
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePressScale
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    RefForwardingComponent
} from "../../types";
import {
    Check as CheckIcon
} from "lucide-react-native";
import Svg, {
    Pattern,
    Rect,
    Defs
} from "react-native-svg";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    formatRgba
} from "../../helpers/color";
import Tooltip from "../tooltip";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const ColorSwatch: RefForwardingComponent<View, IColorSwatchProps> = ({
    isTooltipEnabled: isTooltipEnabledProp,
    surfaceColor: surfaceColorProp,
    "aria-checked": ariaChecked,
    "aria-label": ariaLabelProp,
    "aria-hidden": ariaHidden,
    isDisabled = false,
    role = "button",
    size = "medium",
    customLocalize,
    customTheme,
    isSelected,
    onFocus,
    onPress,
    onBlur,
    color,
    style,
    title,
    ...props
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const checkerId = useId().replace(/[^a-zA-Z0-9]/g, "");

    const isPressable = !!onPress;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled: isDisabled || !isPressable,
        onFocus,
        onBlur
    });

    const pressScaleStyle = usePressScale({
        customTheme,
        isDisabled,
        isPressed
    });

    const surface = surfaceColorProp ?? colors.content.container.default;

    const parsedColor = useMemo(() => parseColorSwatchColor({
        color
    }), [
        color
    ]);

    const parsedSurface = useMemo(() => parseColorSwatchColor({
        color: surface,
        fallback: {
            alpha: 1,
            rgb: [
                1,
                1,
                1
            ]
        }
    }), [
        surface
    ]);

    const parsedLight = useMemo(() => parseColorSwatchColor({
        color: colors.content.container.default
    }), [
        colors.content.container.default
    ]);

    const parsedDark = useMemo(() => parseColorSwatchColor({
        color: colors.content.container.inverse
    }), [
        colors.content.container.inverse
    ]);

    const isEdge = getColorSwatchIsEdge({
        surface: parsedSurface.rgb,
        color: parsedColor
    });

    const checkColor = getColorSwatchInkIsLight({
        background: parsedColor.rgb,
        light: parsedLight.rgb,
        dark: parsedDark.rgb
    }) ? colors.content.container.default : colors.content.container.inverse;

    const currentSize = getColorSwatchSize({
        controlHeights,
        iconSizes,
        spaces,
        size
    });

    const label = ariaLabelProp ?? title ?? getColorSwatchLabel({
        color: parsedColor,
        localize
    });

    const {
        container: containerDynamicStyle,
        color: colorDynamicStyle,
        clip: clipDynamicStyle,
        edge: edgeDynamicStyle
    } = useStyles({
        fill: formatRgba(parsedColor),
        isSelected: !!isSelected,
        isFocusVisible,
        shapeConfigs,
        currentSize,
        isDisabled,
        isHovered,
        radiuses,
        borders,
        surface,
        colors,
        isEdge
    });

    const hitArea = getHitArea({
        visualSize: currentSize.size,
        pointerType,
        touchTargets
    });

    const isTooltipEnabled = isTooltipEnabledProp ?? (!!title && isPressable);

    const content = <>
        <View
            style={[
                stylesheet.clip,
                clipDynamicStyle
            ]}
        >
            {
                parsedColor.alpha < 1 && <Svg
                    style={stylesheet.layer}
                    aria-hidden={true}
                    height="100%"
                    width="100%"
                >
                    <Defs>
                        <Pattern
                            height={spaces.spacingXs * 2}
                            patternUnits="userSpaceOnUse"
                            width={spaces.spacingXs * 2}
                            id={checkerId}
                        >
                            <Rect
                                fill={colors.content.container.raised}
                                height={spaces.spacingXs * 2}
                                width={spaces.spacingXs * 2}
                            />
                            <Rect
                                fill={colors.content.border.default}
                                height={spaces.spacingXs}
                                width={spaces.spacingXs}
                            />
                            <Rect
                                fill={colors.content.border.default}
                                height={spaces.spacingXs}
                                width={spaces.spacingXs}
                                x={spaces.spacingXs}
                                y={spaces.spacingXs}
                            />
                        </Pattern>
                    </Defs>
                    <Rect
                        fill={`url(#${checkerId})`}
                        height="100%"
                        width="100%"
                    />
                </Svg>
            }
            <View
                style={[
                    stylesheet.layer,
                    colorDynamicStyle
                ]}
            />
            {
                isEdge && <View
                    style={[
                        stylesheet.layer,
                        edgeDynamicStyle
                    ]}
                />
            }
            {
                !!isSelected && <CheckIcon
                    size={currentSize.iconSize}
                    color={checkColor}
                />
            }
        </View>
    </>;

    if(!isPressable) {
        const decorativeProps = (ariaHidden ?? (!title && !ariaLabelProp)) ? {
            "aria-hidden": true
        } : {
            "aria-label": label,
            role: "img" as const
        };

        return <View
            {...props}
            {...decorativeProps}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            ref={ref}
        >
            {content}
        </View>;
    }

    const pressable = <AnimatedPressable
        {...props}
        {...interactionHandlers}
        hitSlop={Math.max(0, (hitArea.minHeight - currentSize.size) / 2)}
        aria-pressed={role === "button" ? isSelected : undefined}
        aria-checked={role === "radio" ? ariaChecked : undefined}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle,
            pressScaleStyle
        ]}
        accessibilityLabel={label}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        aria-label={label}
        onPress={onPress}
        role={role}
        ref={ref}
    >
        {content}
    </AnimatedPressable>;

    if(Platform.OS !== "web" || !isTooltipEnabled || !title) {
        return pressable;
    }

    return <Tooltip
        customLocalize={customLocalize}
        customTheme={customTheme}
        title={title}
        side="top"
    >
        {pressable}
    </Tooltip>;
};
export default forwardRef(ColorSwatch);
